import React, { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { SubmitHandler } from 'react-hook-form';
import { useAuthState } from '@vis-auth/react';
import { EveRoutes } from '@ver-uds/e-verify';
import LoaderIcon from 'uswds/dist/img/loader.svg';
import { AccountType, CompanyInfo } from 'eve-account-client';
import { useAccountQuery, useCompanyInfoMutation } from '../services';
import FullWidthLayout from '../layouts/FullWidthLayout';
import ErrorBar from '../components/ErrorBar';
import ErrorConstants from '../constants/ErrorConstants';
import { scrollToTop } from '../utils/util';
import CompanyInformationForm, {
  CompanyInformationSubmitData,
} from '../components/Enrollment/CompanyInformation/CompanyInformationForm';
import FormVariant from '../components/Enrollment/FormVariant';

export interface EditInformationProps {
  isCorporateAdmin?: boolean;
}

const EditCompanyInformationPage = ({ isCorporateAdmin = false }: EditInformationProps): JSX.Element => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const history = useHistory();
  const [mutate] = useCompanyInfoMutation();

  const { user } = useAuthState();

  const { subAccountId } = useParams<{ subAccountId?: string }>();

  // eslint-disable-next-line camelcase
  const accountId = subAccountId || (user?.account_id as string);
  const { isLoading: accountIsLoading, data: accountData } = useAccountQuery(accountId);

  let doneUrl: string = isCorporateAdmin ? EveRoutes.CORPORATE_PROFILE_VIEW : EveRoutes.COMPANY_PROFILE_VIEW;

  if (subAccountId) {
    const doneUrlPath =
      accountData?.type === AccountType.CHILD
        ? EveRoutes.CORPORATE_COMPANY_LOCATIONS_CHILD_PROFILE_VIEW
        : EveRoutes.COMPANY_CLIENTS_CLIENT_PROFILE_VIEW;
    doneUrl = generatePath(doneUrlPath, { subAccountId: accountId });
  }

  const pageTitle = 'Edit Company Information';

  const onSubmit: SubmitHandler<CompanyInformationSubmitData> = async (data) => {
    const submitData = {
      accountId: accountId || '',
      companyInfo: {
        ...(accountData?.companyInfo as CompanyInfo),
        ...data.companyInfoData,
      },
    };
    await mutate(submitData, {
      onSuccess: () => {
        history.push(doneUrl);
      },
      onError: () => {
        setErrorMessage(ErrorConstants.GENERAL_ERROR);
        scrollToTop();
      },
    });
  };
  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      pageTitle: 'E-Verify - Edit Company Information Page',
    });
  }, []);
  return (
    <FullWidthLayout title={pageTitle}>
      <section className="padding-y-4">
        <ErrorBar errorMessage={errorMessage} />
        <h1>{pageTitle}</h1>
        {accountIsLoading ? (
          <div className="margin-top-5 margin-bottom-5">
            Loading… <img src={LoaderIcon} alt="" className="height-2" />
          </div>
        ) : (
          <CompanyInformationForm
            variantType={FormVariant.EDIT}
            isCorporateAdmin={isCorporateAdmin}
            companyInfoData={accountData?.companyInfo}
            cancelUrl={doneUrl}
            categoryType={accountData?.companyInfo.employerCategory?.category}
            onSubmitForm={onSubmit}
          />
        )}
      </section>
    </FullWidthLayout>
  );
};

export default EditCompanyInformationPage;
